import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useLoaderStore } from './loader.store';
import * as tagsApi from '../api/tags.api';
import _ from 'lodash';
import { TAG_TYPES } from '@/utils/constants';

export const useTagsStore = defineStore('tags', () => {
    const tags = ref([]);

    const tagsPerTypeForEntityCRUD = computed(() => {
        const flattened = _.flatMap(tags.value, (item) => item.type.map((t) => ({ ...item, type: t })));
        let grouped = _.groupBy(flattened, 'type');

        Object.values(TAG_TYPES).forEach((type) => {
            if (!grouped[type]) {
                grouped[type] = [];
            }
        });

        const systemTags = tags.value.filter((tag) => tag.system);

        // add system tags to each type array (after grouping) and remove duplicates
        grouped = _.mapValues(grouped, (items) => _(items).concat(systemTags).uniqBy('id').value());

        return _.mapValues(grouped, (items) => items.map((item) => ({ ...item, type: tags.value.find((tag) => tag.id === item.id)?.type })));
    });

    const systemEnvironmentTagsPerType = computed(() => {
        return _.mapValues(tagsPerType.value, (items) => items.filter((item) => item.system || item.type.includes('environment')));
    });

    const tagsPerType = computed(() => {
        const flattened = _.flatMap(tags.value, (item) => item.type.map((t) => ({ ...item, type: t })));
        const grouped = _.groupBy(flattened, 'type');

        Object.values(TAG_TYPES).forEach((type) => {
            if (!grouped[type]) {
                grouped[type] = [];
            }
        });

        return _.mapValues(grouped, (items) => items.map((item) => ({ ...item, type: tags.value.find((tag) => tag.id === item.id)?.type })));
    });

    const loader = useLoaderStore();

    return {
        tags,
        all: computed(() => {
            return _.sortBy(tags.value, (tag) => (tag.system ? 0 : 1));
        }),
        perType: tagsPerType,
        tagsPerTypeForEntityCRUD,
        systemEnvironmentTagsPerType,
        fetch: async function ({ tag_type = null, tag_ids = null } = {}) {
            loader.activateLoading('tags.fetch');

            try {
                if (tag_type?.length && tag_type.every((type) => tagsPerType.value[type]?.length)) {
                    return { tags: tags.value };
                }

                const response = await tagsApi.fetchTags({
                    tag_type,
                    tag_ids
                });

                let allTags = _(tags.value || [])
                    .concat(response.tags)
                    .uniqBy('id')
                    .value();

                // if (tag_type) {
                //     // Scenario 1: We have a tag_type, attach the requested tag_type to all tags that have ALWAYS_INCLUDED_TAGS
                //     allTags = allTags.map((tag) => ({
                //         ...tag,
                //         type: tag.type.some((t) => ALWAYS_INCLUDED_TAGS.includes(t))
                //             ? _(tag.type).concat(tag_type).uniq().value() // Add the requested tag_type if any of ALWAYS_INCLUDED_TAGS exist
                //             : tag.type
                //     }));
                // } else {
                //     // Scenario 2: No tag_type provided, merge tag types from tags with ALWAYS_INCLUDED_TAGS and other tags
                //     const tagsWithAlwaysIncluded = allTags.filter((tag) => tag.type.some((t) => ALWAYS_INCLUDED_TAGS.includes(t)));

                //     const otherTagsTypes = _.flatMap(
                //         allTags.filter((tag) => !tag.type.some((t) => ALWAYS_INCLUDED_TAGS.includes(t))),
                //         (tag) => tag.type
                //     );

                //     allTags = allTags.map((tag) => ({
                //         ...tag,
                //         type: tagsWithAlwaysIncluded.includes(tag) ? _(tag.type).concat(otherTagsTypes).uniq().value() : tag.type
                //     }));
                // }

                tags.value = allTags;

                return response;
            } catch (error) {
                console.error(error);
            } finally {
                loader.deactivateLoading('tags.fetch');
            }
        },
        addTag: async function (tag) {
            loader.activateLoading('tags.add');
            try {
                const response = await tagsApi.addTag(tag);
                tags.value.unshift(response);

                return response;
            } catch (error) {
                console.error(error);
            } finally {
                loader.deactivateLoading('tags.add');
            }
        },
        updateTag: async function (updatedTag) {
            loader.activateLoading('tags.update');
            loader.activateLoading(`tags.update.${updatedTag.id}`);
            try {
                const response = await tagsApi.updateTag(updatedTag);

                // update the tag and keep reactivity
                const index = tags.value.findIndex((tag) => tag.id === updatedTag.id);
                tags.value.splice(index, 1, response);

                return response;
            } catch (error) {
                console.error(error);
            } finally {
                loader.deactivateLoading('tags.update');
                loader.deactivateLoading(`tags.update.${updatedTag.id}`);
            }
        },
        deleteTag: async function (tagId) {
            loader.activateLoading('tags.delete');
            loader.activateLoading(`tags.delete.${tagId}`);
            try {
                const response = await tagsApi.deleteTag(tagId);
                tags.value = tags.value.filter((tag) => tag.id !== tagId);

                return response;
            } catch (error) {
                console.error(error);
            } finally {
                loader.deactivateLoading('tags.delete');
                loader.deactivateLoading(`tags.delete.${tagId}`);
            }
        }
    };
});
